<template>
  <label
    class="checkbox flex relative pointer brdr-none"
    :class="{ 'disabled': disabled }"
  >
    <input
      :id="inputId"
      class="absolute pointer m0"
      :disabled="disabled"
      :name="name"
      :checked="value"
      type="checkbox"
      @keyup.enter="$emit('click')"
      @click="$emit('click')"
      @blur="$emit('blur')"
      @change="$emit('change', $event.target.checked)"
    >
    <div class="checkbox-icon lh22 relative inline-block vertical-middle brdr-1 c-bg-white" />
    <span class="checkbox-text inline-block vertical-middle no-transform c-body regular fs14 lh16 pl10 pl15-desktop "><slot /></span>
    <validation-messages
      v-if="validations"
      :validations="validations"
    />
  </label>
</template>

<script>
import ValidationMessages from './ValidationMessages.vue'

export default {
  name: 'BaseCheckbox',
  components: {
    ValidationMessages
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    id: {
      type: String,
      required: false,
      default: null
    },
    value: {
      type: Boolean,
      required: false,
      default: false
    },
    validations: {
      type: Array,
      default: () => []
    },
    name: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    inputId () {
      return this.id || `input_${this._uid}`
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~theme/css/variables/grid';
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/helpers/mixins/hover';
@import '~theme/css/variables/components';

.checkbox {
  user-select: none;
  appearance: none;

  @include desktop {
    &:hover:not(.disabled) {
      input:not(:checked) ~ .checkbox-icon {
        border-color: color(black);
      }
    }
  }

  &-text {
    color: color(other);

    @include desktop {
      font-size: map-get($checkbox-text-font-size, desktop);
      padding-left: map-get($checkbox-space-between, desktop);
      line-height: map-get($checkbox-size, desktop);
    }
  }

  &-icon {
    height: map-get($checkbox-size, default);
    width: map-get($checkbox-size, default);
    min-width: map-get($checkbox-size, default);
    border-radius: 5px;
    border-color: color(other);

    @include transition(border-color, background-color);

    @include desktop {
      height: map-get($checkbox-size, desktop);
      width: map-get($checkbox-size, desktop);
      min-width: map-get($checkbox-size, desktop);
    }

    &::after {
      /* use !important to prevent issues with browser extensions that change fonts */
      font-family: 'icomoon', sans-serif !important;
      speak: none;
      font-style: normal;
      font-weight: normal;
      font-variant: normal;
      text-transform: none;
      position: absolute;
      left: - $checkbox-border-size;
      top: - $checkbox-border-size - 1px; // Font rendering issue
      right: - $checkbox-border-size;
      bottom: - $checkbox-border-size;
      font-size: $checkbox-icon-size;
      content: "\e924";
      color: color(white);
      opacity: 0;
      text-align: center;

      @include transition(opacity);

      /* Better Font Rendering =========== */
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
  }

  input:checked ~ &-icon::after {
    opacity: 1;
  }

  input:checked ~ &-icon {
    background-color: color(primary);
    border-color: color(primary);
  }

  &.disabled {
    cursor: not-allowed;

    input:disabled ~ .checkbox-icon {
      background-color: color(grayscale-lighter);
      border-color: color(grayscale-lighter);
      color: color(grayscale-dark);
    }

    input:checked ~ .checkbox-icon {
      background-color: color(grayscale-lighter);
      border-color: color(grayscale-lighter);

      &::after {
        color: color(secondary-hover);
        opacity: 0.2;
      }
    }

    .checkbox-text {
      opacity: 0.5;
    }
  }

  input {
    appearance: none;
    opacity: 0;
    height: 0;
    width: 0;
  }
}
</style>
